import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

function AtendimentoSaudeModal({
  isOpen,
  onClose,
  atendimento,
  movimentacaoId,
  movimentacaoFinalizada,
  onSaveSuccess,
  adolescenteNome,
  isMba = false,
  theme = 'dark',
}) {
  const { hasPermission, hasAnyPermission } = useAuth();

  const canEdit = hasAnyPermission(['atendimentos.create', 'atendimentos.edit']) && !movimentacaoFinalizada;
  const canViewOnly = !canEdit && hasPermission('atendimentos.view');
  const isReadOnly = canViewOnly;
  const statusSeiLabel = isMba ? 'E-SUS preenchido' : 'Status SEI';
  const statusSeiAbertoLabel = isMba ? 'Não' : 'Aberto';
  const statusSeiConcluidoLabel = isMba ? 'Sim' : 'Concluído';

  const [formData, setFormData] = useState({
    status_sei: 'aberto',
    check_entrada: false,
    check_saida: false,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (atendimento) {
      setFormData({
        status_sei: atendimento.status_sei ?? 'aberto',
        check_entrada: !!atendimento.check_entrada,
        check_saida: !!atendimento.check_saida,
      });
    } else if (isOpen) {
      setFormData({
        status_sei: 'aberto',
        check_entrada: false,
        check_saida: false,
      });
    }
  }, [atendimento, isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const val = type === 'checkbox' ? checked : value;

    setFormData((prev) => {
      const next = { ...prev, [name]: val };
      if (name === 'check_entrada' && !val) next.check_saida = false; // cascata
      return next;
    });

    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isReadOnly) return;

    setSaving(true);
    setError(null);
    setValidationErrors({});

    try {
      if (atendimento) {
        await api.put(`/atendimentos-saude/${atendimento.id}`, formData);
      } else {
        await api.post(`/movimentacoes/${movimentacaoId}/atendimentos-saude`, formData);
      }
      onSaveSuccess && onSaveSuccess('Atendimento da Saúde salvo com sucesso!');
      onClose();
    } catch (err) {
      const msg =
        err.userMessage ||
        err.response?.data?.message ||
        'Ocorreu um erro ao salvar o atendimento.';
      setError(msg);
      if (err.validationErrors) setValidationErrors(err.validationErrors);
      if (!err.silence) console.error('Erro ao salvar atendimento da Saúde:', err);
    } finally {
      setSaving(false);
    }
  };

  const renderResumoSomenteLeitura = () => {
    const simNao = (flag) => (flag ? 'Sim' : 'Não');

    return (
      <div className="mt-2">
        <div className="mb-2">
          <strong>Atendimento Iniciado:</strong>{' '}
          <span className={formData.check_entrada ? 'text-success' : 'text-secondary'}>
            {simNao(formData.check_entrada)}
          </span>
        </div>
        <div className="mb-2">
          <strong>Atendimento Finalizado:</strong>{' '}
          <span className={formData.check_saida ? 'text-success' : 'text-secondary'}>
            {simNao(formData.check_saida)}
          </span>
        </div>
        <div className="mb-2">
          <strong>{statusSeiLabel}:</strong>{' '}
          <span className={formData.status_sei === 'concluido' ? 'text-success' : 'text-warning'}>
            {formData.status_sei === 'concluido' ? statusSeiConcluidoLabel : statusSeiAbertoLabel}
          </span>
        </div>
        <div className="mt-3 small text-secondary">
          Visualização somente. Permissão de edição não concedida para este usuário.
        </div>
      </div>
    );
  };

  return (
    <div
      className={`modal fade show dashboard-themed-modal dashboard-themed-modal--${theme}`}
      style={{ display: 'block' }}
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content bg-dark text-light dashboard-themed-modal-content">
          <div className="modal-header">
            <div className="w-100">
              <div className="d-flex justify-content-between align-items-start">
                <h5 className="modal-title atendimento-modal-title">Atendimento da Saúde</h5>
                <button
                  type="button"
                  className={theme === 'light' ? 'btn-close' : 'btn-close btn-close-white'}
                  onClick={onClose}
                  disabled={saving}
                  aria-label="Fechar"
                ></button>
              </div>
              <hr className="mt-2 mb-2" />
              {adolescenteNome && (
                <div className="fw-semibold">
                  Adolescente:{' '}
                  <span className="text-light">{adolescenteNome}</span>
                </div>
              )}
            </div>
          </div>

          <div className="modal-body">
            <form onSubmit={handleSubmit} noValidate>
              {error && <div className="alert alert-danger">{error}</div>}

              {isReadOnly ? (
                renderResumoSomenteLeitura()
              ) : (
                <>
                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="check_entrada_saude"
                      name="check_entrada"
                      checked={formData.check_entrada}
                      onChange={handleChange}
                      disabled={saving}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="check_entrada_saude"
                    >
                      Atendimento Iniciado
                    </label>
                  </div>

                  <div className="form-check form-switch mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="check_saida_saude"
                      name="check_saida"
                      checked={formData.check_saida}
                      onChange={handleChange}
                      disabled={!formData.check_entrada || saving}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="check_saida_saude"
                    >
                      Atendimento Finalizado
                    </label>
                  </div>

                  <div className="mb-1 d-flex align-items-center gap-2">
                    <label htmlFor="status_sei_saude" className="form-label mb-0 flex-shrink-0 small">
                      {statusSeiLabel}:
                    </label>
                    <select
                      id="status_sei_saude"
                      name="status_sei"
                      className="form-select form-select-sm bg-dark text-light border-secondary"
                      style={{ borderRadius: '6px' }}
                      value={formData.status_sei}
                      onChange={handleChange}
                      disabled={saving}
                    >
                      <option value="aberto">{statusSeiAbertoLabel}</option>
                      <option value="concluido">{statusSeiConcluidoLabel}</option>
                    </select>
                  </div>

                  {/* Somente para quem pode editar */}
                  <small className="text-secondary d-block mt-2">
                    Para finalizar, o atendimento deve estar &quot;Iniciado&quot;.
                  </small>
                </>
              )}
            </form>
          </div>

          <div className="modal-footer">
            {canEdit ? (
              <>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onClose}
              >
                Fechar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AtendimentoSaudeModal;
